import { useEffect, useRef, useState } from 'react';
import { importLibrary } from '@googlemaps/js-api-loader';
import { configureGoogleMapsLoader } from '../../lib/googleMapsRuntime';
import {
  streetViewRingCacheKey,
  streetViewSessionCache,
} from '../../lib/googleMapsSessionCache';
import {
  SITE_PROBE_BEARINGS,
  SITE_PROBE_RINGS,
  SITE_PROBE_TARGET_COUNT,
  labelSiteViewpoints,
  offsetPoint,
  type LatLngPoint,
  type SiteViewpoint,
  type SiteViewpointCandidate,
} from '../../lib/employerSiteViewpoints';

export type SiteViewpointsStatus = 'idle' | 'loading' | 'ready' | 'none' | 'error';

export type SiteViewpointsResult = {
  status: SiteViewpointsStatus;
  viewpoints: SiteViewpoint[];
  error: string | null;
  /** Building the viewpoints were computed for — ignore if stale vs current. */
  buildingKey: string | null;
};

function buildingKeyOf(point: LatLngPoint): string {
  return `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`;
}

function isHardStreetViewFailure(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  const upper = message.toUpperCase();
  return (
    upper.includes('OVER_QUERY_LIMIT') ||
    upper.includes('REQUEST_DENIED') ||
    upper.includes('UNKNOWN_ERROR') ||
    upper.includes('INVALID_REQUEST') ||
    upper.includes('NETWORK') ||
    upper.includes('FAILED TO FETCH') ||
    upper.includes('BILLING') ||
    upper.includes('API KEY')
  );
}

function isZeroResults(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return message.toUpperCase().includes('ZERO_RESULTS');
}

type RingProbeHit = SiteViewpointCandidate | null;

async function probeRing(
  service: google.maps.StreetViewService,
  center: LatLngPoint,
  ring: { offsetM: number; radiusM: number },
  StreetViewSource: typeof google.maps.StreetViewSource,
): Promise<{ hits: RingProbeHit[]; hardError: string | null }> {
  const cacheKey = streetViewRingCacheKey({
    lat: center.lat,
    lng: center.lng,
    ringOffsetM: ring.offsetM,
    ringRadiusM: ring.radiusM,
  });

  const cached = streetViewSessionCache.get(cacheKey) as
    | { hits: RingProbeHit[]; hardError: string | null }
    | undefined;
  if (cached) return cached;

  return streetViewSessionCache.getOrLoad(cacheKey, async () => {
    let hardError: string | null = null;
    const hits = await Promise.all(
      SITE_PROBE_BEARINGS.map(async (bearing) => {
        try {
          const response = await service.getPanorama({
            location: offsetPoint(center, bearing, ring.offsetM),
            radius: ring.radiusM,
            source: StreetViewSource.OUTDOOR,
          });
          const location = response.data?.location;
          const latLng = location?.latLng;
          if (!location?.pano || !latLng) return null;
          const imageDate =
            typeof (response.data as { imageDate?: string } | undefined)?.imageDate === 'string'
              ? (response.data as { imageDate?: string }).imageDate
              : null;
          return {
            panoId: location.pano,
            location: { lat: latLng.lat(), lng: latLng.lng() },
            description: location.description ?? location.shortDescription ?? null,
            ringOffsetM: ring.offsetM,
            imageDate: imageDate?.trim() || null,
          } satisfies SiteViewpointCandidate;
        } catch (err) {
          if (isZeroResults(err)) return null;
          if (isHardStreetViewFailure(err) && !hardError) {
            hardError =
              err instanceof Error
                ? err.message
                : 'Street View lookup failed (quota, billing, or network).';
          }
          return null;
        }
      }),
    );
    // Do not cache hard failures — allow retry after key/billing fixes.
    if (hardError && hits.every((h) => h == null)) {
      throw Object.assign(new Error(hardError), {
        streetViewHardError: hardError,
      });
    }
    return { hits, hardError: null };
  }) as Promise<{ hits: RingProbeHit[]; hardError: string | null }>;
}

/**
 * Free Street View metadata lookups around the building. Adaptive rings expand
 * only until enough distinct panoramas are found. Results are tied to the
 * building they were computed for so Street View never briefly shows another site.
 */
export function useEmployerSiteViewpoints(input: {
  building: LatLngPoint | null;
  enabled: boolean;
}): SiteViewpointsResult {
  const { building, enabled } = input;
  const lat = building?.lat ?? null;
  const lng = building?.lng ?? null;
  const generationRef = useRef(0);

  const [state, setState] = useState<SiteViewpointsResult>({
    status: 'idle',
    viewpoints: [],
    error: null,
    buildingKey: null,
  });

  useEffect(() => {
    if (!enabled || lat == null || lng == null) {
      setState({ status: 'idle', viewpoints: [], error: null, buildingKey: null });
      return;
    }
    if (!configureGoogleMapsLoader()) {
      setState({
        status: 'error',
        viewpoints: [],
        error: 'Google Maps API key is not configured.',
        buildingKey: null,
      });
      return;
    }

    const center: LatLngPoint = { lat, lng };
    const key = buildingKeyOf(center);
    const generation = ++generationRef.current;
    setState({ status: 'loading', viewpoints: [], error: null, buildingKey: key });

    void (async () => {
      try {
        const { StreetViewService, StreetViewSource } = await importLibrary('streetView');
        const service = new StreetViewService();

        const candidatesByPano = new Map<string, SiteViewpointCandidate>();
        let hardError: string | null = null;

        for (const ring of SITE_PROBE_RINGS) {
          if (generation !== generationRef.current) return;
          try {
            const { hits } = await probeRing(service, center, ring, StreetViewSource);
            for (const hit of hits) {
              if (!hit) continue;
              const existing = candidatesByPano.get(hit.panoId);
              if (!existing) {
                candidatesByPano.set(hit.panoId, hit);
              } else if (!existing.imageDate && hit.imageDate) {
                candidatesByPano.set(hit.panoId, { ...existing, imageDate: hit.imageDate });
              }
            }
          } catch (err) {
            const tagged = err as { streetViewHardError?: string };
            hardError =
              tagged.streetViewHardError ??
              (err instanceof Error ? err.message : 'Street View lookup failed.');
            break;
          }

          if (candidatesByPano.size >= SITE_PROBE_TARGET_COUNT) break;
        }

        if (generation !== generationRef.current) return;

        if (candidatesByPano.size === 0 && hardError) {
          setState({
            status: 'error',
            viewpoints: [],
            error: hardError.includes('OVER_QUERY_LIMIT') || hardError.includes('REQUEST_DENIED')
              ? `Street View unavailable: ${hardError}. This is a Maps API/billing issue, not missing coverage.`
              : hardError,
            buildingKey: key,
          });
          return;
        }

        const candidates = [...candidatesByPano.values()];
        const viewpoints = labelSiteViewpoints(center, candidates);
        setState({
          status: viewpoints.length > 0 ? 'ready' : 'none',
          viewpoints,
          error: null,
          buildingKey: key,
        });
      } catch (err) {
        if (generation !== generationRef.current) return;
        setState({
          status: 'error',
          viewpoints: [],
          error: err instanceof Error ? err.message : 'Street View lookup failed.',
          buildingKey: key,
        });
      }
    })();

    return () => {
      generationRef.current += 1;
    };
  }, [enabled, lat, lng]);

  return state;
}
